/**
 * IspsLibrarySection — Phase 152.AA.
 *
 * Renders the teacher's saved Individual Student Plans (ISPs) on the
 * Library "Individual Student Plans" tab. Plans auto-save server-side
 * on generation (see `routes/individual_student_plan.py`) so there is
 * no save action on the generator page itself.
 *
 * Per-row actions:
 *   - Open Plan → deep-links into the ISP detail (the generator route
 *     with the saved plan re-rendered)
 *   - Delete   → soft confirmation, then DELETE /api/individual-student-plan/{id}
 *
 * Mirrors the Classroom Support PlansLibrarySection visual language so
 * the two new tabs feel like siblings.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Trash2, User2 } from "lucide-react";
import ConfirmDeleteDialog from "./ConfirmDeleteDialog";
import LibraryRecentStrip from "./LibraryRecentStrip";

const apiBase = () => `${process.env.REACT_APP_BACKEND_URL || ""}/api`;

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year: "numeric",
    });
  } catch {
    return "";
  }
}

export default function IspsLibrarySection() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  const fetchPlans = useCallback(async () => {
    try {
      setLoading(true);
      const r = await fetch(`${apiBase()}/individual-student-plan/list`, {
        credentials: "include",
      });
      if (r.status === 403) {
        // Pro+ gate. Treat as empty list — the Library tab will still
        // render and the teacher sees a friendly empty state.
        setPlans([]);
        return;
      }
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      setPlans(Array.isArray(data?.plans) ? data.plans : []);
    } catch (err) {
      console.error("Failed to fetch ISP list:", err);
      setPlans([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPlans();
  }, [fetchPlans]);

  const handleDelete = async (planId) => {
    setDeletingId(planId);
    try {
      const r = await fetch(`${apiBase()}/individual-student-plan/${planId}`, {
        method: "DELETE",
        credentials: "include",
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      setPlans((prev) => prev.filter((p) => p.id !== planId));
      toast.success("Plan deleted.");
    } catch (err) {
      toast.error("Couldn't delete plan", {
        description: err?.message || "Please try again.",
      });
    } finally {
      setDeletingId(null);
      setConfirmDelete(null);
    }
  };

  const hasPlans = plans.length > 0;
  const planLabel = useMemo(
    () => (plans.length === 1 ? "1 plan" : `${plans.length} plans`),
    [plans.length],
  );

  if (loading) {
    return (
      <div className="ae-card p-10 text-center" data-testid="isps-section-loading">
        <Loader2 className="h-5 w-5 animate-spin inline-block mr-2 text-[#4a6984]" />
        <span className="text-[#4a6984]/70 font-medium">Loading plans…</span>
      </div>
    );
  }

  if (!hasPlans) {
    return (
      <div className="ae-card p-10 text-center" data-testid="isps-section-empty">
        <h3 className="font-extrabold text-xl text-[#4a6984]">
          No individual student plans yet
        </h3>
        <p className="mt-2 text-[#4a6984]/80">
          Generated plans live here, ready to view, export, or delete.
        </p>
        <Link
          to="/individual-student-plan"
          data-testid="isps-section-empty-cta"
          className="ae-cta inline-flex items-center gap-2 mt-5 px-5 py-2.5 rounded-md font-extrabold"
        >
          Create a plan
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="isps-section">
      <LibraryRecentStrip
        testid="isps-recent-strip"
        items={[...plans]
          .sort((a, b) => {
            const ta = a?.created_at ? Date.parse(a.created_at) : 0;
            const tb = b?.created_at ? Date.parse(b.created_at) : 0;
            return tb - ta;
          })
          .slice(0, 3)
          .map((p) => ({
            id: p.id,
            title: p.student_label || "Student",
            subtitle: [
              p.grade
                ? (String(p.grade).toLowerCase().startsWith("grade")
                    ? p.grade
                    : `Grade ${p.grade}`)
                : "",
              p.subject || "",
              p.reporting_period || "",
            ]
              .filter(Boolean)
              .join(" · "),
            dateLabel: formatDate(p.created_at),
            href: `/individual-student-plan?plan=${encodeURIComponent(p.id)}`,
          }))}
      />
      <div className="flex items-center justify-between px-1">
        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#4a6984]/70">
          {planLabel}
        </p>
        <Link
          to="/individual-student-plan"
          className="text-sm font-bold text-[#2a7d6b] hover:text-[#1c5a4e] transition"
          data-testid="isps-section-new-cta"
        >
          + New plan
        </Link>
      </div>

      <ul className="space-y-2" data-testid="isps-section-list">
        {plans.map((p) => {
          const label = p.student_label || "Student";
          const meta = [
            p.grade ? (String(p.grade).toLowerCase().startsWith("grade") ? p.grade : `Grade ${p.grade}`) : "",
            p.subject || "",
            p.reporting_period || "",
          ].filter(Boolean).join("  ·  ");
          const goal = (p.learning_goal || "").trim();
          return (
            <li
              key={p.id}
              data-testid={`isp-row-${p.id}`}
              className="ae-card p-4 flex items-start gap-4 hover:shadow-sm transition-shadow"
            >
              <div className="flex-shrink-0 mt-0.5 p-2 rounded-md bg-gradient-to-br from-[#96ded1]/40 to-[#96ded1]/10">
                <User2 className="h-5 w-5 text-[#1c2d3d]" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-baseline justify-between gap-3">
                  <Link
                    to={`/individual-student-plan?plan=${encodeURIComponent(p.id)}`}
                    data-testid={`isp-open-${p.id}`}
                    className="font-extrabold text-[#1c2d3d] hover:text-[#2a7d6b] transition truncate"
                  >
                    {label}
                  </Link>
                  <span className="text-[10px] font-bold uppercase tracking-wide text-[#4a6984]/55 flex-shrink-0">
                    {formatDate(p.created_at)}
                  </span>
                </div>
                {meta && (
                  <p className="text-xs text-[#4a6984]/75 mt-0.5 truncate">{meta}</p>
                )}
                {goal && (
                  <p className="text-sm text-[#4a6984] mt-1 line-clamp-2">{goal}</p>
                )}
              </div>
              <button
                type="button"
                onClick={() => setConfirmDelete(p)}
                disabled={deletingId === p.id}
                data-testid={`isp-delete-${p.id}`}
                className="flex-shrink-0 p-2 rounded-md text-[#4a6984]/60 hover:text-[#b85b5b] hover:bg-[#fdf3f3] transition disabled:opacity-40"
                aria-label="Delete plan"
              >
                {deletingId === p.id ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Trash2 className="h-4 w-4" />
                )}
              </button>
            </li>
          );
        })}
      </ul>

      <ConfirmDeleteDialog
        open={!!confirmDelete}
        onOpenChange={(open) => !open && setConfirmDelete(null)}
        onConfirm={() => confirmDelete && handleDelete(confirmDelete.id)}
        title="Delete this plan?"
        description={
          confirmDelete
            ? `"${confirmDelete.student_label || "Student"}" will be permanently removed.`
            : ""
        }
        confirmLabel="Delete plan"
      />
    </div>
  );
}
